"use client";
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

interface CriteriaSelectorProps {
  autoplay?: boolean;
  autoplayDelay?: number;
}

export const CriteriaSelector: React.FC<CriteriaSelectorProps> = ({
  autoplay = true,
  autoplayDelay = 2000,
}) => {
  const [selectedMustHaves, setSelectedMustHaves] = useState<Set<string>>(new Set());
  const [selectedPreferences, setSelectedPreferences] = useState<Set<string>>(new Set());
  const [currentStep, setCurrentStep] = useState(0);

  const mustHaves = [
    "Fullstack but front-end heavy",
    "Experience in b2c products",
  ];

  const preferences = [
    "premium pedigree",
  ];

  const allItems = [
    ...mustHaves.map((item) => ({ type: "mustHave", text: item })),
    ...preferences.map((item) => ({ type: "preference", text: item })),
  ];

  useEffect(() => {
    if (!autoplay) return;

    const timer = setTimeout(() => {
      // Start selecting items one by one
      const selectNext = (step: number) => {
        if (step >= allItems.length) {
          // Reset and restart
          setSelectedMustHaves(new Set());
          setSelectedPreferences(new Set());
          setCurrentStep(0);
          setTimeout(() => selectNext(0), autoplayDelay);
          return;
        }

        const item = allItems[step];
        
        if (item.type === "mustHave") {
          setSelectedMustHaves((prev) => {
            const next = new Set(prev);
            next.add(item.text);
            return next;
          });
          setCurrentStep(step + 1);
          setTimeout(() => selectNext(step + 1), 800);
        } else if (item.type === "preference") {
          setSelectedPreferences((prev) => {
            const next = new Set(prev);
            next.add(item.text);
            return next;
          });
          setCurrentStep(step + 1);
          setTimeout(() => selectNext(step + 1), 800);
        }
      };

      selectNext(0);
    }, autoplayDelay);

    return () => clearTimeout(timer);
  }, [autoplay, autoplayDelay]);

  const toggleMustHave = (item: string) => {
    setSelectedMustHaves((prev) => {
      const next = new Set(prev);
      if (next.has(item)) {
        next.delete(item);
      } else {
        next.add(item);
      }
      return next;
    });
  };

  const togglePreference = (item: string) => {
    setSelectedPreferences((prev) => {
      const next = new Set(prev);
      if (next.has(item)) {
        next.delete(item);
      } else {
        next.add(item);
      }
      return next;
    });
  };


  return (
    <div className="w-full bg-white p-6 rounded-lg border border-gray-200 h-full">
      <div className="space-y-6">
        {/* Must Haves Section */}
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Must haves:</h4>
          <div className="flex flex-wrap gap-2">
            {mustHaves.map((item) => {
              const isSelected = selectedMustHaves.has(item);
              return (
                <motion.button
                  key={item}
                  onClick={() => toggleMustHave(item)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={cn(
                    "px-4 py-2 rounded-full text-xs font-medium transition-all",
                    isSelected
                      ? "bg-gray-100 text-gray-700 border-2 border-brand-blue"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-transparent"
                  )}
                >
                  {item}
                </motion.button>
              );
            })}
          </div>
        </div>

        {/* Preferences Section */}
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Preferences:</h4>
          <div className="flex flex-wrap gap-2">
            {preferences.map((item) => {
              const isSelected = selectedPreferences.has(item);
              return (
                <motion.button
                  key={item}
                  onClick={() => togglePreference(item)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={cn(
                    "px-4 py-2 rounded-full text-xs font-medium transition-all",
                    isSelected
                      ? "bg-gray-100 text-gray-700 border-2 border-brand-blue"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-transparent"
                  )}
                >
                  {item}
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

